import { SENT, EXCLUDED_TAGS, SENTINEL_REGEX } from './constants'
import { needsDetached, createDetachedBadge, scheduleBadgeLayout } from './badges'
import { excludeByAncestor } from './domUtils'
import { ensureListPanel, updateListPanel } from './listPanel'
import { getOverlayMeta } from './globals'
import { computeAltAriaAnnotation } from './altAriaCommon'

/**
 * createTextScanner
 * Scans the live document for:
 *  - Instrumented sentinel strings (already marked translation keys / missing keys)
 *  - Unknown translation keys (provided externally)
 *  - Probable hard‑coded English strings (heuristic)
 * Wraps matching text nodes in spans with data attributes so an i18n debug UI can render badges.
 * Also annotates alt / aria-label values for accessibility-related text.
 *
 * englishValues: Set of known English translation values (used to detect hard‑coded strings)
 * unknownTKeys: Set of keys that were looked up but not found in translation catalogs
 */

export function createTextScanner(englishValues, unknownTKeys) {
  const seen = new WeakSet()

  // Replace a text node with a container span; preserves DOM position & text while adding metadata
  function wrapTextNode(textNode, key, customMissingLabel, isMissing = false) {
    if (!textNode?.parentNode) return
    const span = document.createElement('span')
    span.className = isMissing ? 'i18n-key-missing' : 'i18n-key-wrapper'
    let label = key
    if (!isMissing) span.setAttribute('data-i18n-key', key)
    else {
      if (customMissingLabel) label = customMissingLabel
      else label = `${key} - missing`
      // Suppress visible label for hard-coded placeholders; keep outline only
      if (label === 'HARD-CODED') {
        span.classList.add('i18n-silent-hc')
      } else {
        span.setAttribute('data-i18n-missing', label)
      }
    }
    textNode.parentNode.replaceChild(span, textNode)
    span.appendChild(textNode)
    seen.add(textNode)
    if (isMissing && span.classList.contains('i18n-silent-hc')) return
    if (needsDetached(span, isMissing)) {
      createDetachedBadge(span, { label, missing: !!isMissing })
    }
  }

  function annotateAltAndAriaInline() {
    const excludeTags = (EXCLUDED_TAGS || []).map((t) => String(t).toUpperCase())
    const candidates = document.querySelectorAll('img[alt], [aria-label], [role=img][aria-label]')
    const meta = getOverlayMeta() || {}
    candidates.forEach((el) => {
      if (!el.closest('main, [data-i18n-scope]')) return
      if (el.classList.contains('i18n-key-alt')) return
      if (el.textContent && el.textContent.trim().length > 0) return
      if (excludeByAncestor(el, excludeTags)) return
      const altText = el.getAttribute('alt') || el.getAttribute('aria-label')
      const res = computeAltAriaAnnotation(altText, meta)
      if (!res) return
      const { label, isHardCoded, isOkBoth } = res
      el.classList.add('i18n-key-alt')
      if (isHardCoded) el.classList.add('hard-coded')
      if (isOkBoth) el.classList.add('ok')
      el.setAttribute('data-i18n-alt', label)
      if (needsDetached(el, false)) createDetachedBadge(el, { label, alt: true, hardCoded: isHardCoded, ok: isOkBoth })
    })
  }

  function scan() {
    // Ensure panel exists before scanning so we can populate after
    ensureListPanel?.()
    const excludeTags = (EXCLUDED_TAGS || []).map((t) => String(t).toUpperCase())
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        if (!node.parentElement) return NodeFilter.FILTER_REJECT
        if (seen.has(node)) return NodeFilter.FILTER_REJECT
        if (!node.textContent) return NodeFilter.FILTER_REJECT
        const parent = node.parentElement
        // Only apply MISSING related wrappers (unknown key / hard-coded / MISS sentinel) when node is under <main>
        const isInScope = !!parent.closest('main, [data-i18n-scope]')
        let excluded = false
        if (excludeTags.length) {
          let el = parent
          while (el) {
            if (excludeTags.includes(el.tagName)) {
              excluded = true
              break
            }
            el = el.parentElement
          }
        }
        if (excluded) {
          const rawEx = node.textContent
          // Even excluded nodes might contain sentinel markup; strip it so UI shows clean text
          if (rawEx.includes(SENT.ORANGE_PREFIX) || rawEx.includes(SENT.MISS_PREFIX)) {
            const keyMatch = SENTINEL_REGEX.exec(rawEx)
            if (keyMatch) node.textContent = keyMatch[3]
          }
          seen.add(node)
          return NodeFilter.FILTER_REJECT
        }
        if (parent.classList?.contains('i18n-key-wrapper') || parent.classList?.contains('i18n-key-missing'))
          return NodeFilter.FILTER_REJECT
        if (parent.classList?.contains('i18n-key-badge')) return NodeFilter.FILTER_REJECT
        if (parent.closest?.('.i18n-key-badge')) return NodeFilter.FILTER_REJECT
        if (/^\s+$/.test(node.textContent)) return NodeFilter.FILTER_REJECT // whitespace only
        const raw = node.textContent
        if (raw.includes(SENT.ORANGE_PREFIX) || raw.includes(SENT.MISS_PREFIX)) {
          // If it's a MISS sentinel but outside <main>, we'll strip but not wrap later; accept so we can process & strip.
          if (raw.includes(SENT.MISS_PREFIX) && !isInScope) return NodeFilter.FILTER_ACCEPT
          return NodeFilter.FILTER_ACCEPT // sentinel encoded
        }
        const trimmed = raw.trim()
        if (unknownTKeys.has(trimmed)) {
          if (!isInScope) return NodeFilter.FILTER_REJECT
          return NodeFilter.FILTER_ACCEPT
        }
        if (!englishValues.has(trimmed) && /[a-zA-Z]/.test(trimmed) && trimmed.length > 2) {
          if (!isInScope) return NodeFilter.FILTER_REJECT
          return NodeFilter.FILTER_ACCEPT
        }
        return NodeFilter.FILTER_REJECT
      },
    })
    const toWrap = []
    while (walker.nextNode()) toWrap.push(walker.currentNode)
    toWrap.forEach(processTextNode)
    // After wrapping text nodes, annotate alt/aria elements (function takes no params)
    annotateAltAndAriaInline()
    scheduleBadgeLayout()
    // Update off-screen panel with current problematic items
    updateListPanel?.(collectOffscreenAnnotated())
  }

  // --- helpers extracted from scan to reduce complexity ---
  function processTextNode(textNode) {
    const content = textNode.textContent || ''
    if (handleSentinelNode(textNode, content)) return
    const trimmed = content.trim()
    const isInScope = !!textNode.parentElement?.closest('main, [data-i18n-scope]')
    if (handleUnknownKeyNode(textNode, trimmed, isInScope)) return
    handleHardCodedNode(textNode, trimmed, isInScope)
  }

  function handleSentinelNode(textNode, content) {
    if (!content.includes(SENT.ORANGE_PREFIX) && !content.includes(SENT.MISS_PREFIX)) return false
    const keyMatch = SENTINEL_REGEX.exec(content)
    if (!keyMatch) return false
    const [, type, exactKey, valuePart] = keyMatch
    textNode.textContent = valuePart // display readable value only, not sentinel wrapper
    const isMissing = type === 'MISS'
    const isInScope = !!textNode.parentElement?.closest('main, [data-i18n-scope]')
    if (!isInScope) {
      seen.add(textNode)
      return true
    }
    wrapTextNode(textNode, exactKey, null, isMissing)
    if (!isMissing) {
      const span = textNode.parentElement?.closest('.i18n-key-wrapper')
      if (span) span.dataset.i18nStatus = 'orange'
    }
    return true
  }

  function handleUnknownKeyNode(textNode, trimmed, isInScope) {
    if (!unknownTKeys.has(trimmed)) return false
    if (!isInScope) {
      seen.add(textNode)
      return true
    }
    wrapTextNode(textNode, trimmed, null, true)
    return true
  }

  function handleHardCodedNode(textNode, trimmed, isInScope) {
    if (englishValues.has(trimmed)) return false
    if (!isInScope) {
      seen.add(textNode)
      return true
    }
    wrapTextNode(textNode, 'hard-coded', 'HARD-CODED', true)
    return true
  }

  return { scan, seen }
}

// Collect orange & missing elements that are off-screen or hidden for the mini panel
function collectOffscreenAnnotated() {
  const items = []
  const candidates = [...document.querySelectorAll('.i18n-key-wrapper[data-i18n-status="orange"], .i18n-key-missing')]
  const vh = window.innerHeight || document.documentElement.clientHeight
  const vw = window.innerWidth || document.documentElement.clientWidth
  candidates.forEach((el) => {
    if (!document.body.contains(el)) return
    if (el.classList.contains('i18n-silent-hc')) return // skip silent hard-coded outlines
    const rect = el.getBoundingClientRect()
    const offscreen = rect.bottom < 0 || rect.top > vh || rect.right < 0 || rect.left > vw
    const styles = window.getComputedStyle(el)
    const hidden = styles.display === 'none' || styles.visibility === 'hidden' || parseFloat(styles.opacity) === 0
    if (!(offscreen || hidden)) return
    let type
    if (el.classList.contains('i18n-key-missing')) type = 'missing'
    else if (el.dataset.i18nStatus === 'orange') type = 'orange'
    else return
    let key = el.getAttribute('data-i18n-key') || ''
    let value = ''
    if (type === 'orange') {
      value = (el.textContent || '').trim()
    } else if (type === 'missing') {
      const missingAttr = el.getAttribute('data-i18n-missing') || ''
      if (missingAttr.includes(' - ')) key = missingAttr.split(' - ')[0]
      value = (el.textContent || '').trim()
    }
    if (!key) key = '(unknown)'
    if (value.length > 120) value = `${value.slice(0, 117)}…`
    items.push({ key, value, type, el })
  })
  const seenPairs = new Set()
  return items.filter((it) => {
    const k = `${it.type}|${it.key}`
    if (seenPairs.has(k)) return false
    seenPairs.add(k)
    return true
  })
}
